import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { ReactComponent as CloseIcon } from '@ubeya/shared-web/assets/close-big.svg';
import { ReactComponent as ChevronRightIcon } from '@ubeya/shared-web/assets/chevron-right.svg';
import { ImagesCarousel, Loader as BaseLoader } from '@ubeya/shared-web/components';
import { FILE_TYPE_ICON_MAP } from '../helpDeskConstants';
import VideoPreviewThumb from './VideoPreviewThumb';

const AttachmentPreviewWrapper = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

const AttachmentPreviewArea = styled.div`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 8px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const PreviewScrollButton = styled.button`
  position: absolute;
  top: 0;
  bottom: 0;
  ${({ $direction }) => ($direction === 'start' ? 'left: 0;' : 'right: 0;')}
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 100%;
  padding: 0;
  border: none;
  background: ${({ theme }) => theme.colors.white}CC;
  cursor: pointer;

  svg {
    /* rotate() is not mirrored by stylis-plugin-rtl, so the chevron has to be flipped by hand:
       it points at the container edge the button sits on. */
    transform: ${({ $direction, $isRtl }) => (($direction === 'start') === !!$isRtl ? 'none' : 'rotate(180deg)')};
  }

  path {
    fill: ${({ theme }) => theme.colors.grey4};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.white}E6;
  }
`;

const Loader = styled(BaseLoader)`
  position: absolute;
  bottom: 4px;
  right: 4px;
`;

const PreviewCard = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${({ $isMedia }) => ($isMedia ? 0 : '11px 30px 11px 11px')};
  border-radius: 6px;
  background: ${({ theme, $isMedia }) => ($isMedia ? 'transparent' : theme.colors.white)};
  border: ${({ theme, $isMedia }) => ($isMedia ? 'none' : `1px solid ${theme.colors.grey2}`)};
  font-size: 13px;
  width: ${({ $isMedia }) => ($isMedia ? '60px' : '230px')};
  min-height: ${({ $isMedia }) => ($isMedia ? '60px' : '50px')};
`;

const PreviewImageThumb = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  cursor: pointer;
`;

const PreviewFileIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 36px;
    height: 36px;
  }
`;

const PreviewFileNameGroup = styled.span`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const PreviewFileName = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

const PreviewFileType = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.grey4};
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: ${({ theme }) => theme.colors.grey1};

  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  svg {
    width: 10px;
    height: 10px;
  }

  path {
    fill: ${({ theme }) => theme.colors.grey4};
  }

  &:hover {
    opacity: 0.8;
  }
`;

const SCROLL_STEP = 176;

const ComposerAttachmentPreview = ({ attachments, onRemove }) => {
  const { t, isRtl } = useContext(ConfigContext);
  const scrollAreaRef = useRef(null);
  const [scrollState, setScrollState] = useState({
    canScrollToStart: false,
    canScrollToEnd: false
  });
  const [carouselIndex, setCarouselIndex] = useState();

  const carouselViews = useMemo(
    () =>
      attachments
        .filter((file) => (file.isImage || file.isVideo) && file.previewUrl)
        .map((file) => ({ id: file.id, source: file.previewUrl, ...(file.isVideo ? { videoUrl: true } : {}) })),
    [attachments]
  );

  const handleMediaClick = useCallback(
    (fileId) => {
      const viewIdx = carouselViews.findIndex((view) => view.id === fileId);
      if (viewIdx >= 0) setCarouselIndex(viewIdx);
    },
    [carouselViews]
  );

  const updateScrollState = useCallback(() => {
    const el = scrollAreaRef.current;
    if (!el) {
      setScrollState({ canScrollToStart: false, canScrollToEnd: false });
      return;
    }

    const maxScroll = el.scrollWidth - el.clientWidth;
    const scrolled = Math.abs(el.scrollLeft);

    setScrollState({
      canScrollToStart: scrolled > 1,
      canScrollToEnd: scrolled < maxScroll - 1
    });
  }, []);

  const handleScroll = useCallback(
    (direction) => {
      const el = scrollAreaRef.current;
      if (!el) return;

      const delta = direction === 'end' ? SCROLL_STEP : -SCROLL_STEP;
      el.scrollBy({
        left: isRtl ? -delta : delta,
        behavior: 'smooth'
      });
    },
    [isRtl]
  );

  useEffect(() => {
    const updateAfterLayout = () => requestAnimationFrame(updateScrollState);
    updateAfterLayout();
    window.addEventListener('resize', updateAfterLayout);

    return () => {
      window.removeEventListener('resize', updateAfterLayout);
    };
  }, [attachments.length, updateScrollState]);

  if (attachments.length === 0) return null;

  return (
    <AttachmentPreviewWrapper>
      {scrollState.canScrollToStart && (
        <PreviewScrollButton type="button" $direction="start" $isRtl={isRtl} onClick={() => handleScroll('start')}>
          <ChevronRightIcon />
        </PreviewScrollButton>
      )}
      <AttachmentPreviewArea ref={scrollAreaRef} onScroll={updateScrollState}>
        {attachments.map((file) => {
          const { Icon: FileIconComponent, labelKey: fileTypeLabelKey } = FILE_TYPE_ICON_MAP[file.type] || {};

          return (
            <PreviewCard key={file.id} $isMedia={file.isImage || file.isVideo}>
              {file.isImage && file.previewUrl ? (
                <PreviewImageThumb src={file.previewUrl} alt={file.name} onClick={() => handleMediaClick(file.id)} />
              ) : file.isVideo && file.previewUrl ? (
                <VideoPreviewThumb src={file.previewUrl} alt={file.name} onClick={() => handleMediaClick(file.id)} />
              ) : (
                <>
                  {FileIconComponent && (
                    <PreviewFileIcon>
                      <FileIconComponent />
                    </PreviewFileIcon>
                  )}
                  <PreviewFileNameGroup>
                    <PreviewFileName>{file.name}</PreviewFileName>
                    {fileTypeLabelKey && <PreviewFileType>{t(fileTypeLabelKey)}</PreviewFileType>}
                  </PreviewFileNameGroup>
                </>
              )}
              {file.uploading && <Loader size="small" />}
              <RemoveButton type="button" onClick={() => onRemove(file.id)}>
                <CloseIcon />
              </RemoveButton>
            </PreviewCard>
          );
        })}
      </AttachmentPreviewArea>
      {scrollState.canScrollToEnd && (
        <PreviewScrollButton type="button" $direction="end" $isRtl={isRtl} onClick={() => handleScroll('end')}>
          <ChevronRightIcon />
        </PreviewScrollButton>
      )}
      <ImagesCarousel views={carouselViews} currentIndex={carouselIndex} onClose={() => setCarouselIndex()} />
    </AttachmentPreviewWrapper>
  );
};

export default ComposerAttachmentPreview;
